import { useEffect, useState } from "react";
import { Routes, Route, Navigate, NavLink, useNavigate } from "react-router-dom";
import AdminPacks from "./AdminPacks";
import AdminCards from "./AdminCards";
import AdminCardForm from "./AdminCardForm";
import AdminPackForm from "./AdminPackForm";
import AdminImport from "./AdminImport";
import AdminExport from "./AdminExport";
import AdminLogin from "./AdminLogin";
import { adminWhoami, adminLogout } from "./adminApi";
import { Button } from "../components/Button";
import { useLanguage } from "../state/language";
import { isApiMode } from "../lib/apiMode";

/** Auth state for the admin shell.
 *   "unknown"  — initial; whoami in flight. Renders a brief spinner.
 *   "authed"   — whoami returned a role; render the admin routes.
 *   "guest"    — API mode and whoami returned 401; render <AdminLogin>.
 *   "static"   — static mode (no backend); always authed, no gate.
 */
type AuthState = "unknown" | "authed" | "guest" | "static";

export default function AdminLayout() {
  const nav = useNavigate();
  const { t } = useLanguage();
  const [auth, setAuth] = useState<AuthState>(
    isApiMode() ? "unknown" : "static",
  );

  useEffect(() => {
    if (!isApiMode()) return; // static mode: no auth check, just render
    let cancelled = false;
    (async () => {
      try {
        const me = await adminWhoami();
        if (cancelled) return;
        setAuth(me ? "authed" : "guest");
      } catch {
        if (!cancelled) setAuth("guest");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (auth === "unknown") {
    return (
      <div className="pp-screen px-4 bg-[#0b0b14] flex items-center justify-center">
        <div className="text-sm opacity-50">…перевірка сесії</div>
      </div>
    );
  }

  if (auth === "guest") {
    return <AdminLogin onSuccess={() => setAuth("authed")} />;
  }

  async function handleLogout() {
    try {
      await adminLogout();
    } catch {
      // even if API call fails, locally go back to guest
    }
    setAuth(isApiMode() ? "guest" : "static");
  }

  return (
    <div className="pp-screen px-4 bg-[#0b0b14]">
      <header className="flex items-center gap-3 pt-3 pb-2">
        <Button variant="ghost" size="sm" onClick={() => nav("/")}>
          ←
        </Button>
        <h2 className="font-display text-xl font-bold flex-1">Admin</h2>
        {isApiMode() && (
          <button
            type="button"
            onClick={() => void handleLogout()}
            className="text-[10px] uppercase tracking-wider rounded-full bg-white/10 hover:bg-white/20 border border-white/15 px-2 py-1"
            title="Вийти з адмінки"
          >
            ⎋ вийти
          </button>
        )}
        <div className="text-xs opacity-50">v0.1</div>
      </header>
      <nav className="flex gap-2 mb-3 text-sm overflow-x-auto pb-2">
        <Tab to="/admin">Packs</Tab>
        <Tab to="/admin/import">Import CSV</Tab>
        <Tab to="/admin/export">Export</Tab>
      </nav>
      <div className="flex-1 overflow-y-auto">
        <Routes>
          <Route index element={<AdminPacks />} />
          {/* Order matters — "pack/new" must come BEFORE "pack/:packId"
              or React Router treats "new" as a packId. */}
          <Route path="pack/new" element={<AdminPackForm mode="new" />} />
          <Route path="pack/:packId/edit-pack" element={<AdminPackForm mode="edit" />} />
          <Route path="pack/:packId" element={<AdminCards />} />
          <Route path="pack/:packId/new" element={<AdminCardForm mode="new" />} />
          <Route path="pack/:packId/edit/:cardId" element={<AdminCardForm mode="edit" />} />
          <Route path="import" element={<AdminImport />} />
          <Route path="export" element={<AdminExport />} />
          <Route path="login" element={<Navigate to="/admin" replace />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </div>
      <div className="text-[10px] text-white/40 text-center pt-2">{t("settings_admin")}</div>
    </div>
  );
}

function Tab({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end={to === "/admin"}
      className={({ isActive }) =>
        `px-3 py-1.5 rounded-full whitespace-nowrap ${
          isActive ? "bg-white text-black font-semibold" : "bg-white/10 text-white"
        }`
      }
    >
      {children}
    </NavLink>
  );
}
